/* Shared fit matcher: does what a shopper typed name one listed housing or part?
 *
 * One implementation for the product page's three fit boxes, the cross-reference page
 * and site search (scripts/test-pdp-fit-check.mjs runs this file as it is).
 *
 * What counts as a match:
 *   - A part number matches whole. PA75 is not PA75SV and C150 is not C1500: a prefix or
 *     a fragment never confirms a fit.
 *   - A housing matches when every word typed is a word of its name, however the model is
 *     spaced or hyphenated ("CCP-420", "CCP 420", "PXC75", "Swim Clear C4030"), and when the
 *     model's digits are typed without its letter code beside the family name
 *     ("Clean & Clear Plus 420" for "Clean & Clear Plus CCP420").
 *   - A brand alone, a family name without its model, a bare number ("425") and a word or
 *     number from a condition in parentheses ("1998" from "(after November 1998)") never
 *     confirm a housing.
 */
(function (root) {
  'use strict';

  function normalize(value) {
    return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  function tokenize(value) {
    return (value || '').toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  }

  // Misspellings and shorthand, token by token (docs/SEARCH_DISCOVERY_SYNONYMS.md).
  var TOKEN_ALIASES = {
    JACUSSI: 'JACUZZI', JACUZI: 'JACUZZI', JACCUZZI: 'JACUZZI',
    STARRITE: 'STARITE', HAYWOOD: 'HAYWARD', HAYWORD: 'HAYWARD',
    PENTIAR: 'PENTAIR', PENTAIRE: 'PENTAIR', TRICLOP: 'TRICLOPS',
    CLEANCLEAR: 'CLEAN CLEAR', CC420: 'CLEAN CLEAR PLUS 420', TC330: 'TRICLOPS 330'
  };

  // Dropped from what the shopper typed only; a listed name keeps every word.
  var STOPWORDS = { AND: 1, THE: 1, FOR: 1, WITH: 1, FILTER: 1, FILTERS: 1, CARTRIDGE: 1, CARTRIDGES: 1, REPLACEMENT: 1 };

  function queryTokens(raw) {
    var out = [];
    tokenize(raw).forEach(function (token) {
      tokenize(TOKEN_ALIASES[token] || token).forEach(function (piece) {
        if (!STOPWORDS[piece]) out.push(piece);
      });
    });
    return out;
  }

  // One listed housing or part. `display` is the name as the page prints it; `brand` the
  // key of the column it sits in ("pentair", "sta-rite"); `housing` true for a filter
  // housing, false for a part number. Words inside parentheses are a condition of the
  // entry, not its name, and are remembered as such.
  function entry(display, options) {
    var o = options || {};
    var tokens = [];
    var paren = [];
    var depth = 0;
    String(display || '').toUpperCase().split(/([()])/).forEach(function (chunk) {
      if (chunk === '(') { depth++; return; }
      if (chunk === ')') { depth = Math.max(0, depth - 1); return; }
      tokenize(chunk).forEach(function (token) { tokens.push(token); paren.push(depth > 0); });
    });
    return {
      display: String(display || ''),
      key: normalize(o.key || display),
      tokens: tokens,
      paren: paren,
      brand: tokenize(o.brand || ''),
      housing: !!o.housing,
      ref: o.ref === undefined ? null : o.ref
    };
  }

  // null, or how well the typed tokens name the entry.
  function matchEntry(q, e) {
    var E = e.tokens;
    if (!q.length || !E.length) return null;
    if (e.housing && !q.some(function (t) { return /[A-Z]/.test(t); })) return null;

    var used = {};
    var usedCount = 0;
    var hasIdentifier = false;
    var wordHits = 0;
    var soft = false;

    function take(indexes) {
      var identifier = false;
      indexes.forEach(function (i) {
        if (!used[i]) { used[i] = true; usedCount++; }
        if (!e.paren[i]) {
          if (/\d/.test(E[i])) identifier = true;
          else if (e.brand.indexOf(E[i]) === -1) wordHits++;
        }
      });
      if (identifier) hasIdentifier = true;
    }

    var i = 0;
    while (i < q.length) {
      var qt = q[i];
      var isIdentifier = /\d/.test(qt);
      var span = 1;
      var found = null;
      var x;
      var len;

      x = E.indexOf(qt);
      if (x > -1) found = [x];
      // Typed in pieces, listed as one: "CCP 420" for CCP420, "Swim Clear" for SwimClear.
      for (len = 2; len <= 3 && !found; len++) {
        if (i + len > q.length) break;
        x = E.indexOf(q.slice(i, i + len).join(''));
        if (x > -1) { found = [x]; span = len; }
      }
      // A plain word may be typed short ("hayw").
      for (x = 0; x < E.length && !found && !isIdentifier && qt.length >= 3; x++) {
        if (!/\d/.test(E[x]) && E[x].indexOf(qt) === 0) found = [x];
      }
      // Typed as one, listed in pieces: "PXC75" for PXC-75, "StarClear" for Star-Clear.
      for (x = 0; x < E.length && !found; x++) {
        for (len = 2; len <= 3 && !found; len++) {
          if (x + len <= E.length && E.slice(x, x + len).join('') === qt) {
            found = [];
            for (var k = 0; k < len; k++) found.push(x + k);
          }
        }
      }
      // A housing's model digits typed without its letter code: "420" for CCP420. Counts
      // only beside a word of the housing's own name (checked below).
      if (!found && e.housing && /^\d{2,}$/.test(qt)) {
        var code = new RegExp('^[A-Z]{1,5}' + qt + '$');
        for (x = 0; x < E.length && !found; x++) {
          if (!e.paren[x] && code.test(E[x])) { found = [x]; soft = true; }
        }
      }
      if (!found) {
        // The brand of the entry's own column may be typed too; it names nothing by itself.
        if (!isIdentifier && e.brand.indexOf(qt) > -1) { i++; continue; }
        return null;
      }
      take(found);
      i += span;
    }

    if (soft && wordHits < 1) return null;
    // A part number is named whole: every number group of it is typed ("817" or "0106"
    // alone is not 817-0106). A maker's word inside the label ("Aladdin 17509") and a pack
    // token of a SKU ("4PACK") may be left out.
    if (!e.housing) {
      for (var w = 0; w < E.length; w++) {
        if (!used[w] && !e.paren[w] && /\d/.test(E[w]) && !/^\d+PACK$/.test(E[w])) return null;
      }
    }
    if (!hasIdentifier) {
      // No model or part identifier: the entry has to be named in full.
      var named = 0;
      for (var t = 0; t < E.length; t++) if (!e.paren[t] && !STOPWORDS[E[t]]) named++;
      if (usedCount < named) return null;
    }
    return { soft: soft, extra: E.length - usedCount };
  }

  // Every entry the query names, best first: an exact model before a letterless one,
  // then the entry with the fewest words left over.
  function findAll(raw, entries) {
    var needle = normalize(raw);
    if (!needle) return [];
    var direct = (entries || []).filter(function (e) { return e.key === needle; });
    if (direct.length) return direct;
    var q = queryTokens(raw);
    var hits = [];
    (entries || []).forEach(function (e, order) {
      var m = matchEntry(q, e);
      if (m) hits.push({ e: e, soft: m.soft ? 1 : 0, extra: m.extra, order: order });
    });
    hits.sort(function (a, b) { return a.soft - b.soft || a.extra - b.extra || a.order - b.order; });
    return hits.map(function (h) { return h.e; });
  }

  function find(raw, entries) {
    return findAll(raw, entries)[0] || null;
  }

  // Every cartridge a typed housing name is listed for, across the catalog. One name can
  // belong to two cartridges told apart only by a date or a size ("Clean & Clear Plus 520"
  // is XLS-718 before November 1998 and XLS-706 after), so the best match alone is not the
  // answer. `entries` are housing entries whose `ref.p` lists product indexes (the site
  // index's housings). One { product, name } per product, best match first; `name` is the
  // entry that carries its condition in parentheses when the product has one.
  function listedFor(raw, entries) {
    var out = [];
    var at = {};
    var needle = normalize(raw);
    var found = findAll(raw, entries);
    // A name typed exactly as one cartridge lists it ("Pentair Clean & Clear Plus 520") must not
    // hide the same housing listed on another cartridge with a condition ("... (before November 1998)").
    (entries || []).forEach(function (e) {
      if (found.indexOf(e) === -1 && normalize(e.display.replace(/\([^)]*\)/g, ' ')) === needle) found.push(e);
    });
    found.forEach(function (e) {
      ((e.ref && e.ref.p) || []).forEach(function (p) {
        if (at[p] === undefined) {
          at[p] = out.length;
          out.push({ product: p, name: e.display });
        } else if (out[at[p]].name.indexOf('(') === -1 && e.display.indexOf('(') > -1) {
          out[at[p]].name = e.display;
        }
      });
    });
    return out;
  }

  var api = {
    normalize: normalize,
    tokenize: tokenize,
    queryTokens: queryTokens,
    entry: entry,
    find: find,
    findAll: findAll,
    listedFor: listedFor
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.EfFitMatch = api;
})(typeof window !== 'undefined' ? window : this);
